import React, { useEffect, useState } from "react";
import { css } from "@emotion/react";
import { useNavigate } from "react-router";
import { useAuth, useLinks, useModalConfirmation, useQueryData, useToast } from "hooks";
import { FormattedMessage } from "react-intl";
import { NotificationPreferenceCheckbox } from "components/Notification";
import {
  GET_MISSION_ROLE_APPLICANT,
  GET_MISSION_ROLE_APPLICANT_FOR_CLIENT,
  GET_MISSION_ROLE_APPLICANT_FOR_MEMBER,
  GET_MISSION_ROLE_APPLICANTS_COUNT,
  GET_PROTECTED_MISSION_ROLE_APPLICANT,
} from "graphql/queries";
import { useMutation } from "@apollo/client";
import {
  ADD_MISSION_ROLE_APPLICANT,
  SUBMIT_APPLICATION,
  UPDATE_APPLICANT,
  UPDATE_APPLICANT_STATE,
} from "graphql/mutations";
import {
  APPLICANT_TYPES,
  APPLICATION_STEPS,
  CODE_CHALLENGE_STATUS,
  NOTIFIABLE_APPLICANT_TYPES,
  SORT,
} from "constants/index";
import { colors } from "style";
import { PrimaryLink } from "components/Links";

export default function useMissionRoleApplicant({ id, skip, skipCountQuery = false }) {
  const { toast } = useToast();
  const links = useLinks();
  const { isAdmin, isClient, isShowcase, isCandidate, isMember, canViewEditRate } = useAuth();
  const navigate = useNavigate();
  const { show, hide } = useModalConfirmation();

  const { loading, data, error } = useQueryData({
    queryName: getQueryName(isAdmin, isClient, isShowcase, isMember, isCandidate),
    skip: !id || skip,
    variables: {
      id,
      profileChallengesStatus: CODE_CHALLENGE_STATUS.completed,
      profileChallengesOrderBy: { completedAt: SORT.desc },
      includeSellingPriceEstimates: isClient,
      includeMissionData: isAdmin,
    },
  });

  const [handleAddApplicant, { loading: adding }] = useMutation(ADD_MISSION_ROLE_APPLICANT);
  const [handleUpdateApplicant, { loading: updating }] = useMutation(UPDATE_APPLICANT);
  const [handleSubmitApplication, { loading: submitting }] = useMutation(SUBMIT_APPLICATION);
  const [handleUpdateApplicantState, { loading: updatingState }] = useMutation(UPDATE_APPLICANT_STATE);

  const updateApplicant = (id, params) => {
    handleUpdateApplicant({
      variables: {
        input: {
          id,
          params,
        },
      },
      optimisticResponse: () => ({
        updateMissionRoleApplicant: {
          missionRoleApplicant: {
            id: data?.missionRoleApplicant?.id,
            ...params,
            __typename: "MissionRoleApplicant",
          },
          __typename: "UpdateMissionRoleApplicantPayload",
        },
      }),
    });
  };

  const updateApplicantState = (id, state, shouldNotify = false) => {
    handleUpdateApplicantState({
      variables: {
        input: {
          id,
          state,
          shouldNotify,
        },
      },
    });
  };

  const confirmNotification = async (state) => {
    if (!isAdmin || !NOTIFIABLE_APPLICANT_TYPES.includes(state)) return false;

    let nextShouldNotify = false;

    const NotificationBody = () => {
      const [checked, setChecked] = useState(isAdmin);

      useEffect(() => {
        nextShouldNotify = checked;
      }, [checked]);

      return <NotificationPreferenceCheckbox value={checked} onChange={(_, value) => setChecked(value)} />;
    };

    const confirmed = await show(
      <FormattedMessage id="Missions.Applicants.Notification.ConfirmTitle" />,
      <NotificationBody />,
      { showCancel: true, primaryButtonMessageId: "Global.Confirm" }
    );

    if (!confirmed) return null;

    return nextShouldNotify;
  };

  const addApplicant = async (missionRoleId) => {
    handleAddApplicant({
      variables: {
        input: {
          missionRoleId,
          params: {
            state: APPLICANT_TYPES.pending,
          },
        },
      },
      onCompleted: () => {
        navigate(links.candidate_home);
        toast.success(<FormattedMessage id="Missions.MissionRole.ApplyForm.Success" />);
      },
      onError: () => toast.error(<FormattedMessage id="Missions.MissionRole.ApplyForm.Error" />),
    });
  };

  const submitApplication = async (id, applicationSteps = [], completedSteps = [], hasPreferredRate) => {
    const missingRequiredSteps = applicationSteps.filter(
      (step) => step.required && !completedSteps.includes(step.name)
    );

    const missingOptionalSteps = applicationSteps.filter(
      (step) => !step.required && !completedSteps.includes(step.name)
    );

    const hasMissingRequiredSteps = missingRequiredSteps.length > 0;
    const hasMissingOptionalSteps = missingOptionalSteps.length > 0;

    const response = await show(
      <FormattedMessage id="Missions.Applicants.ApplicationSubmitButton.CTA" />,
      <div css={styles.modalContainer}>
        {!hasPreferredRate && canViewEditRate ? (
          <div css={styles.warningAlert}>
            <p>
              <FormattedMessage id="Missions.Applicants.ApplicationSubmitModal.PreferredRateRequired" />
            </p>

            <PrimaryLink to={`${links.candidate_profile}#preferred-rate`} onClick={hide} withMoreIcon routerEnabled>
              <FormattedMessage id="Missions.Applicants.ApplicationSubmitModal.AddPreferredRate" />
            </PrimaryLink>
          </div>
        ) : hasMissingRequiredSteps ? (
          <div css={styles.warningAlert}>
            <p>
              <FormattedMessage id="Missions.Applicants.ApplicationSubmitModal.RequiredWarning" />
            </p>

            <ul>
              {missingRequiredSteps.map((step) => (
                <li key={step.id}>
                  <FormattedMessage id={`Missions.Applicants.ApplicantProgress.Step.${step.name.toLowerCase()}`} />
                </li>
              ))}
            </ul>
          </div>
        ) : hasMissingOptionalSteps ? (
          <div css={styles.infoAlert}>
            <p>
              <FormattedMessage id="Missions.Applicants.ApplicationSubmitModal.OptionalNotice" />
            </p>

            <ul>
              {missingOptionalSteps.map((step) => (
                <li key={step.id}>
                  <FormattedMessage id={`Missions.Applicants.ApplicantProgress.Step.${step.name.toLowerCase()}`} />
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p css={styles.cleanState}>
            <FormattedMessage id="Missions.Applicants.ApplicationSubmitModal.AllCompletedPrompt" />
          </p>
        )}

        {hasPreferredRate && canViewEditRate && (
          <div css={styles.confirm}>
            <FormattedMessage id="Missions.Applicants.ApplicationSubmitModal.Description1" />
          </div>
        )}
      </div>,
      {
        primaryButtonMessageId: hasMissingRequiredSteps
          ? "Missions.Applicants.ApplicationSubmitModal.SubmitAnyway"
          : "Global.Submit",
        showSaveAsDestructable: hasMissingRequiredSteps,
        showSave: !hasMissingRequiredSteps,
        canSubmit: !canViewEditRate || hasPreferredRate,
      }
    );

    if (!response) return;

    handleSubmitApplication({
      variables: {
        input: {
          id,
          submitProfile: isCandidate,
        },
      },
      onCompleted: () => toast.success(<FormattedMessage id="Missions.Applicants.ApplicationSubmitToast.Success" />),
      onError: () => toast.error(<FormattedMessage id="Missions.Applicants.ApplicationSubmitToast.Error" />),
    });
  };

  const getApplicationSteps = (checklist) => {
    return isCandidate
      ? checklist?.filter(({ name }) =>
          [
            APPLICATION_STEPS.requirements,
            APPLICATION_STEPS.application_rate,
            APPLICATION_STEPS.questionnaire,
            APPLICATION_STEPS.code_challenge,
            APPLICATION_STEPS.video_interview,
          ].includes(name)
        )
      : checklist;
  };

  const {
    loading: loadingApplicants,
    data: dataApplicants,
    error: errorApplicants,
  } = useQueryData({
    queryName: GET_MISSION_ROLE_APPLICANTS_COUNT,
    skip: !data?.missionRoleApplicant?.profile?.id || skipCountQuery,
    variables: {
      filters: {
        profileId: data?.missionRoleApplicant?.profile?.id,
        excludingMissionRoleId: data?.missionRoleApplicant?.missionRole?.id,
      },
    },
  });

  const styles = {
    modalContainer: css`
      display: flex;
      flex-direction: column;
      gap: 20px;
    `,
    warningAlert: css`
      padding: 16px;
      border-radius: 8px;
      background-color: ${colors.lightRed};
      border: 1px solid ${colors.mediumRed};
      color: ${colors.darkRed};
      font-size: 14px;
      line-height: 1.5;

      p {
        margin: 0 0 10px;
        font-weight: 600;
      }

      ul {
        margin: 0;
        padding-left: 20px;
      }

      li {
        padding-left: 2px;
        margin-bottom: 6px;

        &:last-child {
          margin-bottom: 0;
        }
      }
    `,
    infoAlert: css`
      padding: 16px;
      border-radius: 8px;
      background-color: ${colors.lightYellow};
      border: 1px solid ${colors.yellow};
      color: ${colors.purpleRainDark3};
      font-size: 14px;
      line-height: 1.5;

      p {
        margin: 0 0 10px;
        font-weight: 600;
      }

      ul {
        margin: 0;
        padding-left: 20px;
      }

      li {
        padding-left: 2px;
        margin-bottom: 6px;

        &:last-child {
          margin-bottom: 0;
        }
      }
    `,
    cleanState: css`
      margin: 0;
      font-size: 14px;
      line-height: 1.5;
      color: ${colors.grayAnatomyDark};
    `,
    // Submission disclaimer / confirmation text
    confirm: css`
      font-weight: 600;
    `,
  };

  return {
    loading,
    data: data?.missionRoleApplicant || data?.publicMissionRoleApplicant,
    updateApplicant,
    updating,
    submitApplication,
    submitting,
    getApplicationSteps,
    confirmNotification,
    updateApplicantState,
    updatingState,
    addApplicant,
    adding,
    list: {
      loading: loadingApplicants,
      data: dataApplicants,
      error: errorApplicants,
    },
    error,
  };
}

const getQueryName = (isAdmin, isClient, isShowcase, isMember, isCandidate) => {
  switch (true) {
    case isAdmin:
      return GET_MISSION_ROLE_APPLICANT;
    case isClient || isShowcase:
      return GET_MISSION_ROLE_APPLICANT_FOR_CLIENT;
    case isMember || isCandidate:
      return GET_MISSION_ROLE_APPLICANT_FOR_MEMBER;
    default:
      return GET_PROTECTED_MISSION_ROLE_APPLICANT;
  }
};
